import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, Database, TrendingUp, AlertTriangle, RefreshCw,
  Layers, Search, Brain, Target,
  Clock3, ShieldCheck, Lightbulb, UsersRound,
  Briefcase, Lock, Maximize2, Globe2,
} from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import DemoDialog from "@/components/site/DemoDialog";
import AmbientParticles from "@/components/site/AmbientParticles";
import RelatedSolutions from "@/components/site/RelatedSolutions";
import IllustrativePill from "@/components/site/IllustrativePill";
import StandardCTA from "@/components/site/StandardCTA";
import { INVESTORS_IMAGES } from "@/lib/site";

const CHALLENGES = [
  { icon: Database,        title: "Scattered market intelligence", desc: "Critical data is spread across sources and hard to unify." },
  { icon: TrendingUp,      title: "Unclear asset performance",     desc: "Limited visibility into real-time performance and trends." },
  { icon: AlertTriangle,   title: "High risk, low clarity",        desc: "Decisions are made with incomplete data and analysis." },
  { icon: RefreshCw,       title: "Missed opportunities",          desc: "Lack of early signals leads to slower action and lost investment potential." },
];

const SOLUTIONS = [
  { icon: Layers,  title: "Unified market & asset intelligence",   desc: "Aggregate and normalize data across markets, assets, and sources in one view." },
  { icon: Search,  title: "Real-time performance tracking",        desc: "Monitor assets, projects, and portfolios with live dashboards and KPIs." },
  { icon: Brain,   title: "AI-powered insights & risk scoring",    desc: "Identify opportunities and assess risk with predictive analytics and scoring." },
  { icon: Target,  title: "Opportunity detection & alerts",        desc: "Get early signals and automated alerts on high-potential investment opportunities." },
];

const RESULTS = [
  { icon: Clock3,       title: "Smarter investment decisions",  desc: "Make data-driven decisions with confidence and speed." },
  { icon: ShieldCheck,  title: "Lower risk exposure",            desc: "Identify and mitigate risks before they impact returns." },
  { icon: Lightbulb,    title: "Higher portfolio performance",   desc: "Optimize allocations and maximize returns." },
  { icon: UsersRound,   title: "Complete investment visibility", desc: "See everything that matters in one unified platform." },
];

const WHY = [
  { icon: Briefcase,  title: "Built for investment success",    desc: "Designed for investors, funds, family offices, and institutions." },
  { icon: Lock,       title: "Enterprise-grade security",       desc: "Bank-grade security, role-based access, and complete data control." },
  { icon: Maximize2,  title: "Scalable across portfolios",      desc: "From a single asset to a global portfolio — we scale with you." },
  { icon: Globe2,     title: "Global market coverage",          desc: "Access comprehensive data across markets and regions." },
];

function SectionTitle({ children, subtitle, testid }) {
  return (
    <div className="text-center fade-up">
      <h2 data-testid={testid}
        className="font-display text-platinum text-[30px] sm:text-[36px] lg:text-[44px] leading-tight tracking-[-0.018em]">
        {children}
      </h2>
      {subtitle && (
        <p className="mt-4 text-[14px] sm:text-[15px] text-platinum/65 leading-[1.7] max-w-[720px] mx-auto">
          {subtitle}
        </p>
      )}
    </div>
  );
}

function CardIcon({ Icon, tone = "sapphire" }) {
  const isGold = tone === "gold";
  const ring  = isGold ? "rgba(217,165,79,0.55)" : "rgba(96,165,250,0.55)";
  const glow  = isGold ? "rgba(217,165,79,0.42)" : "rgba(96,165,250,0.45)";
  const fill  = isGold ? "rgba(217,165,79,0.10)" : "rgba(96,165,250,0.10)";
  const color = isGold ? "#E5C77F" : "#60A5FA";
  return (
    <div className="w-12 h-12 rounded-full inline-flex items-center justify-center shrink-0"
      style={{
        background: `radial-gradient(circle at 30% 30%, ${fill}, #0E1422 75%)`,
        border: `1px solid ${ring}`,
        boxShadow: `0 0 18px -3px ${glow}, inset 0 0 10px ${fill}`,
      }}>
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
  );
}

export default function InvestorsPage() {
  const navigate = useNavigate();
  const [demoOpen, setDemoOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, []);
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setScrollY(window.scrollY));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, []);

  const heroParallax = `translate3d(0, ${-scrollY * 0.14}px, 0)`;

  return (
    <div className="min-h-screen bg-obsidian text-platinum relative overflow-x-hidden" data-testid="investors-page">
      <div className="fixed inset-0 pointer-events-none z-0" aria-hidden>
        <AmbientParticles count={32} tone="mixed" density={0.55} />
      </div>
      <Navbar onBookDemo={() => setDemoOpen(true)} />

      {/* HERO */}
      <section className="relative pt-[120px] lg:pt-[150px] pb-16 lg:pb-24 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none bg-grid opacity-[0.06]" aria-hidden />
        <div className="absolute -top-32 right-0 w-[820px] h-[820px] pointer-events-none"
          style={{ background: "radial-gradient(closest-side, rgba(96,165,250,0.20), transparent)" }} aria-hidden />
        <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="grid lg:grid-cols-[1fr_1.3fr] gap-8 lg:gap-12 items-center min-h-[560px] lg:min-h-[660px]">
            <div className="fade-up relative z-10">
              <Link to="/solutions" data-testid="investors-back"
                className="inline-flex items-center gap-2 text-[12px] text-platinum/60 hover:text-platinum mb-6 transition-colors">
                <ArrowLeft className="w-3.5 h-3.5" /> Back to Solutions
              </Link>
              <div className="text-[10.5px] tracking-[0.38em] uppercase font-medium">
                <span className="text-sapphire-glow">SOLUTIONS</span>
                <span className="text-platinum/40 mx-2">/</span>
                <span className="text-sapphire-glow">INVESTORS</span>
              </div>
              <h1 className="font-display mt-5 text-platinum text-[44px] sm:text-[56px] lg:text-[64px] xl:text-[68px] leading-[1.04] tracking-[-0.022em]">
                Intelligence That<br />
                <span style={{ textShadow: "0 0 30px rgba(96,165,250,0.25)" }}>Maximizes Returns</span>
              </h1>
              <p className="mt-6 text-[16px] sm:text-[17px] text-platinum/72 leading-[1.7] max-w-[520px]">
                Actionable insights. Smarter decisions.<br />
                Stronger portfolio performance.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-3">
                <button onClick={() => navigate("/get-your-audit-report")} data-testid="investors-hero-audit"
                  className="btn-primary rounded-md h-12 px-6 text-[14px] font-medium inline-flex items-center gap-2">
                  Get Your Audit Report <ArrowRight className="w-4 h-4" />
                </button>
                <button onClick={() => setDemoOpen(true)} data-testid="investors-hero-demo"
                  className="btn-ghost rounded-md h-12 px-6 text-[14px] font-medium inline-flex items-center gap-2">
                  Book a Demo <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Network-wired skyline — homepage full-bleed */}
            <div className="relative lg:-mr-10 xl:-mr-20" data-testid="investors-hero-visual"
              style={{ transform: heroParallax, willChange: "transform" }}>
              <div className="absolute inset-0 pointer-events-none"
                style={{ background: "radial-gradient(60% 55% at 55% 50%, rgba(37,99,235,0.32), transparent 72%)", filter: "blur(48px)" }} aria-hidden />

              <div className="absolute top-[20%] left-[26%] w-1 h-1 rounded-full bg-sapphire-glow twinkle" style={{ animationDelay: "0.3s", boxShadow: "0 0 10px rgba(96,165,250,1)" }} aria-hidden />
              <div className="absolute top-[34%] right-[22%] w-1.5 h-1.5 rounded-full bg-white twinkle" style={{ animationDelay: "1.4s", boxShadow: "0 0 12px rgba(255,255,255,0.95)" }} aria-hidden />
              <div className="absolute bottom-[36%] left-[18%] w-1 h-1 rounded-full bg-sapphire-glow twinkle" style={{ animationDelay: "2.0s", boxShadow: "0 0 10px rgba(96,165,250,1)" }} aria-hidden />
              <div className="absolute top-[16%] right-[38%] w-[5px] h-[5px] rounded-full bg-sapphire-glow twinkle" style={{ animationDelay: "0.7s", boxShadow: "0 0 14px rgba(96,165,250,1)" }} aria-hidden />
              <div className="absolute bottom-[44%] right-[12%] w-1 h-1 rounded-full bg-amber-200 twinkle" style={{ animationDelay: "1.6s", boxShadow: "0 0 10px rgba(252,211,77,0.9)" }} aria-hidden />

              <div className="relative">
                <img src={INVESTORS_IMAGES.hero} alt="Esthera RealityOS — connected investment intelligence across the global skyline"
                  className="block w-full h-[520px] lg:h-[680px] object-cover object-center"
                  style={{
                    WebkitMaskImage: "radial-gradient(120% 110% at 50% 50%, #000 60%, rgba(0,0,0,0.6) 80%, transparent 100%)",
                    maskImage: "radial-gradient(120% 110% at 50% 50%, #000 60%, rgba(0,0,0,0.6) 80%, transparent 100%)",
                  }}
                  draggable="false" />
                <div className="absolute inset-0 pointer-events-none"
                  style={{ background: "linear-gradient(180deg, rgba(10,15,26,0.75) 0%, rgba(10,15,26,0) 18%, rgba(10,15,26,0) 70%, rgba(10,15,26,0.98) 100%)" }} aria-hidden />
                <div className="absolute inset-0 pointer-events-none"
                  style={{ background: "linear-gradient(90deg, rgba(10,15,26,1) 0%, rgba(10,15,26,0.82) 14%, rgba(10,15,26,0.18) 32%, rgba(10,15,26,0) 50%, rgba(10,15,26,0) 78%, rgba(10,15,26,0.55) 100%)" }} aria-hidden />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THE CHALLENGE TODAY */}
      <section className="relative pb-16 lg:pb-20">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <SectionTitle testid="investors-challenge-title"
            subtitle="Investors operate in a fast-moving market with incomplete information and high risk.">
            The Challenge Today
          </SectionTitle>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
            {CHALLENGES.map((c, i) => (
              <div key={c.title} data-testid={`investors-challenge-${i}`}
                className="relative rounded-2xl border border-white/[0.07] p-6 lg:p-7 fade-up hover:border-sapphire-glow/35 transition-all duration-500 min-h-[230px]"
                style={{ background: "linear-gradient(180deg, rgba(15,22,40,0.45) 0%, rgba(10,15,26,0.85) 100%)", animationDelay: `${i * 80}ms` }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 0 30px -8px rgba(96,165,250,0.35)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}>
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/35 to-transparent" />
                <CardIcon Icon={c.icon} tone="sapphire" />
                <div className="mt-5 font-display text-platinum text-[16px] sm:text-[17px] leading-[1.25] tracking-tight">{c.title}</div>
                <p className="mt-2.5 text-[12.5px] sm:text-[13px] text-platinum/60 leading-[1.65]">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW ESTHERA SOLVES IT */}
      <section className="relative pb-16 lg:pb-20">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <SectionTitle testid="investors-solves-title"
            subtitle="EstheraRealityOS unifies your data, surfaces what matters, and empowers smarter investment decisions.">
            How Esthera Solves It
          </SectionTitle>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
            {SOLUTIONS.map((s, i) => (
              <div key={s.title} data-testid={`investors-solution-${i}`}
                className="relative rounded-2xl border border-white/[0.07] p-6 lg:p-7 fade-up hover:border-amber-300/30 transition-all duration-500 min-h-[230px]"
                style={{ background: "linear-gradient(180deg, rgba(20,18,12,0.45) 0%, rgba(10,15,26,0.85) 100%)", animationDelay: `${i * 80}ms` }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 0 30px -8px rgba(217,165,79,0.28)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}>
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-amber-300/35 to-transparent" />
                <CardIcon Icon={s.icon} tone="gold" />
                <div className="mt-5 font-display text-platinum text-[16px] sm:text-[17px] leading-[1.25] tracking-tight">{s.title}</div>
                <p className="mt-2.5 text-[12.5px] sm:text-[13px] text-platinum/60 leading-[1.65]">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REAL RESULTS */}
      <section className="relative pb-16 lg:pb-20">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <SectionTitle testid="investors-results-title"
            subtitle="Intelligence that drives better decisions, stronger performance, and higher returns.">
            Real Results for Investors
          </SectionTitle>
          <div className="mt-10 relative rounded-2xl border border-white/[0.07] p-6 sm:p-10 lg:p-12 overflow-hidden"
            style={{ background: "linear-gradient(180deg, rgba(13,18,32,0.6) 0%, rgba(10,15,26,0.92) 100%)" }}>
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/40 to-transparent" />
            <div className="grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-12 items-center">
              {/* 3D Investment dashboard — keep all KPI labels readable */}
              <div className="relative aspect-square fade-up">
                <IllustrativePill position="top-3 right-3" z="z-20" />
                <div className="absolute inset-[-6%] pointer-events-none"
                  style={{ background: "radial-gradient(closest-side, rgba(37,99,235,0.30), transparent 72%)", filter: "blur(40px)" }} />
                <img src={INVESTORS_IMAGES.results}
                  alt="Investment Intelligence Dashboard (illustrative sample data) — Portfolio Performance +24.6%, Risk Management Low Risk, Market Trends Bullish, Asset Analytics 87%, Opportunity Pipeline $2.4B; Asset Allocation Residential 45% Commercial 25% Mixed Use 15% Land 10% Other 5%; Market Intelligence 32 Markets, 128 Opportunities, $2.4B Pipeline Value; Opportunity Pipeline stages Identified 320, Analyzing 180, Due Diligence 95, Under Contract 42, Closing 18."
                  className="relative w-full h-full object-contain breathe-glow"
                  style={{
                    mixBlendMode: "screen",
                    filter: "brightness(0.85) contrast(2.8) saturate(1.22)",
                    WebkitMaskImage: "radial-gradient(ellipse 96% 94% at 50% 50%, #000 60%, rgba(0,0,0,0.5) 82%, transparent 100%)",
                    maskImage: "radial-gradient(ellipse 96% 94% at 50% 50%, #000 60%, rgba(0,0,0,0.5) 82%, transparent 100%)",
                  }}
                  draggable="false" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8 lg:gap-x-10 lg:gap-y-10">
                {RESULTS.map((r, i) => (
                  <div key={r.title} data-testid={`investors-result-${i}`}
                    className="fade-up" style={{ animationDelay: `${i * 80}ms` }}>
                    <CardIcon Icon={r.icon} tone="sapphire" />
                    <div className="mt-4 font-display text-platinum text-[16px] sm:text-[17px] leading-[1.25]">{r.title}</div>
                    <p className="mt-2 text-[12.5px] sm:text-[13px] text-platinum/60 leading-[1.65] max-w-[260px]">{r.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY ESTHERA FOR INVESTORS — 4 cards in a single row */}
      <section className="relative pb-16 lg:pb-20">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <SectionTitle testid="investors-why-title"
            subtitle="Purpose-built for investors who demand clarity, control, and performance.">
            Why Esthera for Investors
          </SectionTitle>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
            {WHY.map((w, i) => (
              <div key={w.title} data-testid={`investors-why-${i}`}
                className="relative rounded-2xl border border-white/[0.07] p-6 lg:p-7 fade-up hover:border-sapphire-glow/35 transition-all duration-500 min-h-[210px]"
                style={{ background: "linear-gradient(180deg, rgba(15,22,40,0.45) 0%, rgba(10,15,26,0.85) 100%)", animationDelay: `${i * 80}ms` }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 0 30px -8px rgba(96,165,250,0.35)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}>
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/35 to-transparent" />
                <CardIcon Icon={w.icon} tone="sapphire" />
                <div className="mt-5 font-display text-platinum text-[16px] sm:text-[17px] leading-[1.25] tracking-tight">{w.title}</div>
                <p className="mt-2.5 text-[12.5px] sm:text-[13px] text-platinum/60 leading-[1.65]">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AUDIT CTA — uses StandardCTA (uniform site-wide) */}
      <StandardCTA
        testId="investors-cta"
        eyebrow="GET STARTED"
        eyebrowTone="positioning"
        title="See exactly where your investments can perform better."
        highlight="can perform better."
        subtitle="Start with a free investment intelligence audit — no cost, no commitment."
        crystal={INVESTORS_IMAGES.auditCta}
        primaryLabel="Get Your Audit Report"
        primaryHref="/get-your-audit-report"
        secondaryLabel="Book a Demo"
        secondaryHref="/book-demo"
      />

      <RelatedSolutions currentSlug="investors" />
      <Footer />
      <DemoDialog open={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
}
